import * as speechsdk from 'microsoft-cognitiveservices-speech-sdk';
import type { SubscriptionTier } from '../config/subscriptionTiers';

export interface SpeechConfig {
  key: string;
  region: string;
  language: string;
  tier?: SubscriptionTier;
}

export class SpeechService {
  private static instance: SpeechService | null = null;
  private recognizer: speechsdk.SpeechRecognizer | null = null;
  private config: SpeechConfig;
  private onRecognizedCallback: ((text: string) => void) | null = null;
  private isRecognizing: boolean = false;

  private constructor(config: SpeechConfig) {
    this.config = config;
    console.debug('Speech service created for region:', config.region);
  }

  public static getInstance(config: SpeechConfig): SpeechService {
    // Always dispose of the existing instance before creating a new one
    if (SpeechService.instance) {
      SpeechService.instance.dispose();
      SpeechService.instance = null;
    }

    if (!config.key || !config.region) {
      throw new Error('Speech service configuration is invalid');
    }
    SpeechService.instance = new SpeechService(config);
    return SpeechService.instance;
  }

  private setupRecognizer(): boolean {
    try {
      // Clean up existing recognizer if any
      if (this.recognizer) {
        this.recognizer.close();
        this.recognizer = null;
      }

      console.log('Setting up speech recognizer...');
      const speechConfig = speechsdk.SpeechConfig.fromSubscription(
        this.config.key,
        this.config.region
      );
      
      // Configure for optimal recognition
      speechConfig.speechRecognitionLanguage = this.config.language;
      
      // Enable enhanced features for paid tiers
      if (this.config.tier && this.config.tier !== 'FREE') {
        console.log('Using enhanced speech recognition model');
        
        // Enable enhanced recognition model with conversation optimization
        speechConfig.setServiceProperty(
          'speechRecognitionModel',
          'enhanced',
          speechsdk.ServicePropertyChannel.UriQueryParameter
        );

        // Enable conversation mode for better context awareness
        speechConfig.setServiceProperty(
          'contextKey',
          'contact-info',
          speechsdk.ServicePropertyChannel.UriQueryParameter
        );

        // Enable background noise optimization
        speechConfig.setServiceProperty(
          "speechServiceResponse_FilterProfanity",
          "true",
          speechsdk.ServicePropertyChannel.UriQueryParameter
        );

        // Enable dynamic audio processing
        speechConfig.setServiceProperty(
          "speechServiceConnection_EnableAudioProcessing",
          "true",
          speechsdk.ServicePropertyChannel.UriQueryParameter
        );

        // Enable audio quality metrics
        speechConfig.setServiceProperty(
          "speechServiceConnection_EnableAudioQualityMetrics",
          "true",
          speechsdk.ServicePropertyChannel.UriQueryParameter
        );

        // Optimize for contact information recognition
        speechConfig.setProfanity(speechsdk.ProfanityOption.Removed);
        
        // Optimize recognition settings for business context
        speechConfig.setProperty(
          speechsdk.PropertyId.SpeechServiceResponse_RequestWordLevelTimestamps,
          "true"
        );
        
        // Improve recognition confidence
        speechConfig.setProperty(
          speechsdk.PropertyId.SpeechServiceResponse_RequestSentenceBoundary,
          "true"
        );
        
        // Set enhanced timeouts for optimal recognition
        speechConfig.setProperty(
          "SPEECH_CONFIG_ENDPOINT_TIMEOUT_MS",
          "25000"
        );
        
        speechConfig.setProperty(
          speechsdk.PropertyId.SpeechServiceConnection_InitialSilenceTimeoutMs,
          "4000"
        );
        
        speechConfig.setProperty(
          speechsdk.PropertyId.SpeechServiceConnection_EndSilenceTimeoutMs,
          "1500"
        );
        
        speechConfig.setProperty(
          speechsdk.PropertyId.Speech_SegmentationSilenceTimeoutMs,
          "1000"
        );

        // Enable detailed recognition results
        speechConfig.outputFormat = speechsdk.OutputFormat.Detailed;
      } else {
        console.log('Using standard speech recognition model');
        // Standard timeouts for free tier
        speechConfig.setProperty(
          speechsdk.PropertyId.SpeechServiceConnection_InitialSilenceTimeoutMs,
          "3000"
        );
        
        speechConfig.setProperty(
          speechsdk.PropertyId.SpeechServiceConnection_EndSilenceTimeoutMs,
          "1000"
        );
        
        // Use simple output format for free tier
        speechConfig.outputFormat = speechsdk.OutputFormat.Simple;
      }

      // Configure audio input with noise suppression
      const audioConfig = speechsdk.AudioConfig.fromDefaultMicrophoneInput();
      this.recognizer = new speechsdk.SpeechRecognizer(speechConfig, audioConfig);

      // Add comprehensive phrase list after recognizer is created
      if (this.config.tier && this.config.tier !== 'FREE') {
        const phraseList = speechsdk.PhraseListGrammar.fromRecognizer(this.recognizer);
        
        // Contact-related terms
        phraseList.addPhrase("phone");
        phraseList.addPhrase("mobile");
        phraseList.addPhrase("telephone");
        phraseList.addPhrase("cell");
        phraseList.addPhrase("number");
        
        // Email-related terms
        phraseList.addPhrase("email");
        phraseList.addPhrase("mail");
        phraseList.addPhrase("at");
        phraseList.addPhrase("dot");
        phraseList.addPhrase("com");
        phraseList.addPhrase("net");
        phraseList.addPhrase("org");
        phraseList.addPhrase("gmail");
        phraseList.addPhrase("yahoo");
        phraseList.addPhrase("hotmail");
        phraseList.addPhrase("outlook");
        
        // Business-related terms
        phraseList.addPhrase("business");
        phraseList.addPhrase("company");
        phraseList.addPhrase("office");
        phraseList.addPhrase("contact");
        phraseList.addPhrase("website");
        phraseList.addPhrase("address");
        
        // Common connectors
        phraseList.addPhrase("and");
        phraseList.addPhrase("or");
        phraseList.addPhrase("is");
        phraseList.addPhrase("the");
        phraseList.addPhrase("my");

        // Add error handling and recognition events
        this.recognizer.recognizing = (_, e) => {
          // Log interim recognition for debugging
          if (e.result.text) {
            console.debug('Interim recognition:', e.result.text);
          }
        };

        this.recognizer.recognized = (_, e) => {
          if (e.result.properties) {
            try {
              // Get audio quality metrics from the detailed output
              const resultProps = JSON.parse(
                e.result.properties.getProperty(speechsdk.PropertyId.SpeechServiceResponse_JsonResult)
              );
              
              // Extract noise metrics if available
              const audioQuality = resultProps?.AudioQuality || {};
              const noiseLevel = audioQuality.NoiseLevel;
              const signalToNoiseRatio = audioQuality.SignalToNoiseRatio;
              
              if (noiseLevel !== undefined && signalToNoiseRatio !== undefined) {
                console.debug('Audio metrics:', {
                  noiseLevel,
                  signalToNoiseRatio
                });

                // Provide feedback if audio quality is poor
                if (noiseLevel > -20 || signalToNoiseRatio < 10) {
                  console.warn('Poor audio quality detected. Consider:');
                  console.warn('- Moving to a quieter environment');
                  console.warn('- Speaking closer to the microphone');
                  console.warn('- Checking microphone settings');
                }
              }
            } catch (error) {
              console.debug('Could not parse audio quality metrics:', error);
            }
          }
        };

        this.recognizer.canceled = (_, e) => {
          if (e.reason === speechsdk.CancellationReason.Error) {
            console.error(`Speech recognition canceled: ${e.errorDetails}`);
          }
        };
      }

      console.log('Speech recognizer setup complete with config:', {
        language: this.config.language,
        region: this.config.region,
        outputFormat: speechConfig.outputFormat,
        enhanced: this.config.tier !== 'FREE'
      });

      return true;
    } catch (error) {
      console.error('Error setting up recognizer:', error);
      return false;
    }
  }

  public async startRecognizing(): Promise<void> {
    try {
      if (this.isRecognizing) {
        console.log('Already recognizing, ignoring start request');
        return;
      }

      console.log('Starting continuous recognition...');
      this.isRecognizing = true;
      
      // Ensure recognizer is set up
      if (!this.recognizer) {
        if (!this.setupRecognizer()) {
          this.isRecognizing = false;
          throw new Error('Failed to setup recognizer');
        }
      }

      // Start recognition in a way that handles the deferral error
      await new Promise<void>((resolve, reject) => {
        if (!this.recognizer) {
          reject(new Error('Recognizer not initialized'));
          return;
        }

        // Add event handlers before starting
        this.recognizer.recognized = (_, e) => {
          if (e.result.reason === speechsdk.ResultReason.RecognizedSpeech) {
            const text = e.result.text.trim();
            if (text) {
              console.log('Speech recognized (final):', text);
              this.onRecognizedCallback?.(text);
            }
          }
        };

        this.recognizer.recognizing = (_, e) => {
          const text = e.result.text.trim();
          if (text) {
            console.log('Recognizing interim result:', text);
          }
        };

        // Start recognition with proper error handling
        this.recognizer
          .startContinuousRecognitionAsync(
            () => {
              console.log('Recognition started successfully');
              resolve();
            },
            (error) => {
              console.error('Error starting recognition:', error);
              this.isRecognizing = false;
              reject(error);
            }
          );
      });
    } catch (error) {
      this.isRecognizing = false;
      console.error('Error in startRecognizing:', error);
      throw error;
    }
  }

  public async stopRecognizing(): Promise<void> {
    if (!this.recognizer || !this.isRecognizing) {
      return;
    }

    console.log('Stopping speech recognition...');
    try {
      await new Promise<void>((resolve, reject) => {
        this.recognizer?.stopContinuousRecognitionAsync(
          () => {
            console.log('Speech recognition stopped successfully');
            this.isRecognizing = false;
            resolve();
          },
          (error) => {
            console.error('Error stopping recognition:', error);
            this.isRecognizing = false;
            reject(error);
          }
        );
      });
    } catch (error) {
      console.error('Error in stopRecognizing:', error);
      this.isRecognizing = false;
      throw error;
    }
  }

  public onRecognized(callback: (text: string) => void): void {
    this.onRecognizedCallback = callback;
  }

  public dispose(): void {
    try {
      if (this.recognizer) {
        try {
          this.recognizer.stopContinuousRecognitionAsync();
        } catch (err) {
          console.warn('Error stopping recognition during dispose:', err);
        }
        try {
          this.recognizer.close();
        } catch (err) {
          console.warn('Error closing recognizer during dispose:', err);
        }
        this.recognizer = null;
      }
      this.onRecognizedCallback = null;
      SpeechService.instance = null;
    } catch (error) {
      console.error('Error during dispose:', error);
      // Ensure instance is cleared even on error
      SpeechService.instance = null;
    }
  }
}